import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import "../../styles/PluginConfiguration.css";
import openNotification from "components/utils/CustomNotification";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const API_URL_PLUGIN_CONFIG_FILTER = `${process.env.REACT_APP_API_URL}/api/org-plugin-config-filter`;
const API_URL_PLUGIN_CONFIG = `${process.env.REACT_APP_API_URL}/api/org-plugin-config`;

interface PluginField {
  name: string;
  db_key: string;
  type?: string;
  default?: any;
  editable?: boolean;
  category: string;
  method?: string;
  values?: Record<string, any>;
  params?: Record<string, any>;
}

interface PluginConfigState {
  fields: Record<string, PluginField>;
  values: Record<string, any>;
  pluginName: string;
}

const PluginConfiguration: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const [pluginConfigs, setPluginConfigs] = useState<
    Record<string, PluginConfigState>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const privilege = localStorage.getItem("privilege");
  const isSystemAdmin = privilege === "systemadmin";
  const orgDetailsFromNav = location.state?.orgDetails;

  const fetchPluginConfigData = async () => {
    setLoading(true);
    try {
      //const orgUid = localStorage.getItem("org_uid") || "";
      let orgUid = orgDetailsFromNav?.org_uid;

      if (privilege === "orgadmin" && !orgUid) {
        //if you store org_uid in localStorage or user profile
        orgUid = localStorage.getItem("org_uid");
      }

      // Fetch filters (plugin field definitions)
      const filterResp = await axios.get(API_URL_PLUGIN_CONFIG_FILTER, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      const filters = filterResp.data.data || {};

      // Fetch saved org plugin config
      const defaultResp = await axios.get(API_URL_PLUGIN_CONFIG, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
        params: { org_uid: orgUid },
      });

      const pluginConfigArray = defaultResp.data.data?.[0]?.plugin_config || [];

      // Map saved configs by actual plugin name
      const defaultsMap: Record<string, any> = {};
      pluginConfigArray.forEach((plugin: any) => {
        defaultsMap[plugin.name] = {
          enable: plugin.enable ?? "No",
          params: plugin.params || {},
        };
      });

      // Build configs grouped by plugin prefix
      const grouped: Record<string, PluginConfigState> = {};

      Object.entries(filters).forEach(([key, field]) => {
        const pluginField = field as PluginField;
        const pluginPrefix = key.split("_")[0]; // cal / dist

        const pluginName =
          pluginPrefix === "cal"
            ? "step_calories"
            : pluginPrefix === "dist"
              ? "step_distance"
              : pluginPrefix;

        const saved = defaultsMap[pluginName] || {
          enable: undefined,
          params: {},
        };

        const mergedField: PluginField = {
          ...pluginField,
          default:
            pluginField.method === "toggle"
              ? (saved.enable ?? pluginField.default ?? "No")
              : (saved.params?.[pluginField.db_key] ??
                pluginField.default ??
                ""),
          params: {
            ...pluginField.params,
            ...saved.params,
          },
        };

        if (!grouped[pluginPrefix]) {
          grouped[pluginPrefix] = { fields: {}, values: {}, pluginName }; // ADD pluginName here
        }

        grouped[pluginPrefix].fields[key] = mergedField;

        const compoundKey = `${pluginField.name}_${pluginField.db_key}`;
        grouped[pluginPrefix].values[compoundKey] = mergedField.default ?? "";
      });

      setPluginConfigs(grouped);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load plugin config");
    } finally {
      setLoading(false);
    }
  };

  // run it once on mount
  useEffect(() => {
    fetchPluginConfigData();
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);

    try {
      const allPayloads: any[] = [];

      Object.entries(pluginConfigs).forEach(
        ([pluginPrefix, { fields, values }]) => {
          const pluginName =
            pluginPrefix === "cal"
              ? "step_calories"
              : pluginPrefix === "dist"
                ? "step_distance"
                : pluginPrefix;

          const pluginPayload = {
            name: pluginName,
            enable: "No",
            params: {} as Record<string, any>,
          };

          Object.values(fields).forEach((field) => {
            const compoundKey = `${field.name}_${field.db_key}`;

            if (field.method === "toggle") {
              pluginPayload.enable =
                values[compoundKey] ?? field.default ?? "No";
            } else {
              pluginPayload.params[field.db_key] =
                values[compoundKey] ?? field.default ?? "";
            }
          });

          allPayloads.push(pluginPayload);
        },
      );

      const payload = {
        org_uid: localStorage.getItem("org_uid") || "",
        org_plugin_config: allPayloads,
      };

      const response = await axios.put(API_URL_PLUGIN_CONFIG, payload, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.message || "All plugins saved successfully!",
        });

        // refresh only on success
        fetchPluginConfigData();
      } else {
        openNotification({
          type: "error",
          message:
            response.data.data ||
            response.data.message ||
            "Failed to save changes",
        });
      }
    } catch (err: any) {
      openNotification({
        type: "error",
        message:
          err.response?.data?.data ||
          err.response?.data?.message ||
          "Server Error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="plugin-config-page">Loading...</div>;
  if (error) return <div className="plugin-config-page error">{error}</div>;

  const pluginDisplayNames: Record<string, string> = {
    cal: "Plugin 1",
    dist: "Plugin 2",
  };

  const formatPluginName = (name: string): string => {
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="plugin-config-page">
      <div className="plugin-header">Plugin Configuration</div>
      {/* {successMsg && <div className="success-msg">{successMsg}</div>} */}

      {Object.entries(pluginConfigs).map(
        ([plugin, { fields, values, pluginName }], index) => (
          <div key={plugin} className="plugin-block">
            <h3 className="plugin-title">
              {pluginDisplayNames[plugin] || `Plugin ${index + 1}`}
            </h3>
            <ul className="plugin-fields-list">
              {Object.values(fields).map((field) => {
                const compoundKey = `${field.name}_${field.db_key}`;
                return (
                  <li key={compoundKey} className="plugin-field-item">
                    <label className="plugin-label">
                      <span className="plugin-field-name">{field.name}:</span>{" "}
                      {field.method === "toggle" ? (
                        <input
                          type="checkbox"
                          checked={values[compoundKey] === "Yes"}
                          disabled={!field.editable || isSystemAdmin}
                          onChange={(e) =>
                            setPluginConfigs((prev) => ({
                              ...prev,
                              [plugin]: {
                                ...prev[plugin],
                                values: {
                                  ...prev[plugin].values,
                                  [compoundKey]: e.target.checked
                                    ? "Yes"
                                    : "No",
                                },
                              },
                            }))
                          }
                        />
                      ) : field.editable && !isSystemAdmin ? (
                        <input
                          type="text"
                          value={values[compoundKey] || ""}
                          onChange={(e) =>
                            setPluginConfigs((prev) => ({
                              ...prev,
                              [plugin]: {
                                ...prev[plugin],
                                values: {
                                  ...prev[plugin].values,
                                  [compoundKey]: e.target.value,
                                },
                              },
                            }))
                          }
                          className="plugin-input"
                        />
                      ) : (
                        <span className="plugin-readonly">
                          {field.db_key === "name"
                            ? formatPluginName(pluginName)
                            : values[compoundKey]?.toString() || "N/A"}
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
      )}
      {!isSystemAdmin && (
        <div className="save-btn-container">
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="save-btn"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PluginConfiguration;
